/* eslint-disable */

/**
 * Encrypts plaintext using AES-GCM with supplied password, for decryption with aesGcmDecrypt().
 *                                                                      (c) Chris Veness MIT Licence
 *                                                                      optimized for IndexedDB Storage by Jakob Pfab
 *
 * @param   {String} plaintext - Plaintext to be encrypted.
 * @param   {String} password - Password to use to encrypt plaintext.
 * @param   {String} ivPrefix - prefix to store the initialization vector (IV) for the encrypted value
 * @returns {ArrayBuffer} Encrypted ciphertext as ArrayBuffer to store it directly in Indexed DB
 *
 */
export const aesGcmEncrypt = async function (
  plaintext: string,
  password: string,
  ivPrefix = 'prefix',
) {
  //console.time("WEB SUBTLE CRYPTO AES ENCRYPTION CALCULATION TIME");
  //console.time("initializing encryption");
  const pwUtf8 = new TextEncoder().encode(password); // encode password as UTF-8
  const pwHash = await crypto.subtle.digest('SHA-256', pwUtf8); // hash the password

  const iv = crypto.getRandomValues(new Uint8Array(12)); // get 96-bit random iv
  //const iv = new Uint8Array(16);
  const ivStr = Array.from(iv)
    .map((b) => String.fromCharCode(b))
    .join(''); // iv as utf-8 string
  window.localStorage.setItem('x' + '-' + ivPrefix, ivStr);

  const alg = { name: 'AES-GCM', iv: iv }; // specify algorithm to use
  //console.timeEnd("initializing encryption");
  //console.time("importing key");
  const key = await crypto.subtle.importKey('raw', pwHash, alg, false, [
    'encrypt',
  ]); // generate key from pw
  //console.timeEnd("importing key");

  //console.time("encoding plaintext to UTF-8");
  const ptUint8 = new TextEncoder().encode(plaintext); // encode plaintext as UTF-8
  //console.timeEnd("encoding plaintext to UTF-8");

  //console.time("aes encryption time");
  const ctBuffer = await crypto.subtle.encrypt(alg, key, ptUint8); // encrypt plaintext using key

  //console.timeEnd("aes encryption time");
  //console.timeEnd("WEB SUBTLE CRYPTO AES ENCRYPTION CALCULATION TIME");

  return ctBuffer;

  /**
   *
   This Code can be used to get the result as a String,
   but the conversion to string is more expensive than the encryption itself.
   So instead we store the ArrayBuffer directly into IndexedDB as this is supported by IndexedDB
   *
   * */

  /*
  const ctArray = Array.from(new Uint8Array(ctBuffer));// ciphertext as byte array
  //console.timeEnd(" ciphertext as byte array");
  //console.time("ciphertext as string");
  const ctStr = ctArray.map(byte => String.fromCharCode(byte)).join('');             // ciphertext as string
  //console.timeEnd("ciphertext as string");
  return btoa(ivStr+ctStr);
  */ // iv+ciphertext base64-encoded
};

/**
 * Decrypts ciphertext encrypted with aesGcmEncrypt() using supplied password.
 *                                                                     (c) Chris Veness MIT Licence
 *                                                                      optimized for IndexedDB Storage by Jakob Pfab
 *
 * @param   {ArrayBuffer} ciphertext - Ciphertext to be decrypted as ArrayBuffer
 * @param   {String} password - Password to use to decrypt ciphertext.
 * @param   {String} ivPrefix - prefix to store the initialization vector (IV) for the encrypted value
 * @returns {String} Decrypted plaintext.
 *
 * @example
 *   const plaintext = await aesGcmDecrypt(ciphertext, 'pw');
 *   aesGcmDecrypt(ciphertext, 'pw').then(function(plaintext) {});
 */
export const aesGcmDecrypt = async function (
  ciphertext: any,
  password: string,
  ivPrefix = 'prefix',
) {
  const pwUtf8 = new TextEncoder().encode(password); // encode password as UTF-8
  const pwHash = await crypto.subtle.digest('SHA-256', pwUtf8); // hash the password

  // Try the key-specific IV first, then fall back to the legacy 'x-prefix'
  // default used by builds that did not pass ivPrefix to aesGcmEncrypt.
  const ivStr =
    window.localStorage.getItem('x' + '-' + ivPrefix) ??
    window.localStorage.getItem('x-prefix'); // decode base64 iv
  if (!ivStr) throw new Error('Decrypt failed');

  const iv = new Uint8Array(Array.from(ivStr).map((ch) => ch.charCodeAt(0))); // iv as Uint8Array

  const alg = { name: 'AES-GCM', iv: iv }; // specify algorithm to use

  const key = await crypto.subtle.importKey('raw', pwHash, alg, false, [
    'decrypt',
  ]); // generate key from pw

  //const ctStr = atob(ciphertext).slice(16);                                          // decode base64 ciphertext
  //const ctUint8 = new Uint8Array(Array.from(ctStr).map(ch => ch.charCodeAt(0)));     // ciphertext as Uint8Array

  try {
    const plainBuffer = await crypto.subtle.decrypt(alg, key, ciphertext); // decrypt ciphertext using key
    const plaintext = new TextDecoder().decode(plainBuffer); // plaintext from ArrayBuffer
    return plaintext; // return the plaintext
  } catch (e) {
    throw new Error('Decrypt failed');
  }
};
